/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { DateTime, Duration, DurationUnit, Settings } from 'luxon';
import { diagnostics } from './diagnostics';
import { startCase } from '@utils/lodash';

export const formatYMD = (dateString: string | null) => {
  if (!dateString) {
    return dateString;
  }
  const result = DateTime.fromFormat(dateString, 'yyyy-LL-dd').toFormat('LL/dd/yyyy');
  if (result === 'Invalid DateTime') {
    diagnostics.error(new Error(`Invalid DateTime: ${dateString}`));
    return '';
  }
  return result;
};

export const formatMDY = (dateString: string | null) => {
  if (!dateString) {
    return dateString;
  }
  const result = DateTime.fromFormat(dateString, 'LL/dd/yyyy').toFormat('yyyy-LL-dd');
  if (result === 'Invalid DateTime') {
    diagnostics.error(new Error(`Invalid DateTime: ${dateString}`));
    return '';
  }
  return result;
};

export const formatTime = (dateString: string, opts: { withSec?: boolean } = {}): string => {
  const { withSec = true } = opts;
  const result = DateTime.fromISO(dateString).toFormat(`h:mm${withSec ? ':ss' : ''} a ZZZZ'`);
  if (result === 'Invalid DateTime') {
    return dateString;
  }
  return result;
};

export const formatUTCTime = (dateString: string) => {
  const result = DateTime.fromISO(dateString, { zone: 'utc' }).toFormat(`LL/dd/yyyy h:mm:ss a`);
  if (result === 'Invalid DateTime') {
    diagnostics.error(new Error(`Invalid DateTime: ${dateString}`));
    return dateString;
  }
  return result;
};

// takes an array of names and turn the array to a comma/'and' separated string
// ex [ "Bailey Dog", "Happy Frog" ] => "Bailey Dog and Happy Frog"
export const formatNames = (names: string[]): string => {
  const initialNames = names.slice(0, -1).join(', ');
  const lastFullName = names.slice(-1);
  const conjunction = names.length <= 1 ? '' : names.length > 2 ? ', and ' : ' and ';
  return [initialNames, lastFullName].join(conjunction);
};

export const titleCase = (text: string | undefined): string => {
  return startCase((text || '').toLowerCase());
};

export const formatFullTimeStampAtBrowserTimeZone = (isoTime: string): string => {
  return DateTime.fromISO(isoTime).toFormat('LL/dd/yyyy hh:mm a ZZZZ');
};

/** @param yyyyMMDD - date string in iso format YYYY-MM-DD */
export const formatIsoDate = (yyyyMMDD?: string): string => {
  if (!yyyyMMDD) {
    return '';
  }
  return DateTime.fromISO(yyyyMMDD).toUTC().toFormat('LL/dd/yyyy');
};

/** @param yyyyMMDD - date string in iso format YYYY-MM-DD local timezone*/
export const formatIsoDateInLocalTime = (yyyyMMDD?: string): string => {
  if (!yyyyMMDD) {
    return '';
  }
  return DateTime.fromISO(yyyyMMDD).toLocal().toFormat('LL/dd/yyyy');
};

export const formatIsoToLocalTimeWithTimeZone = (
  iso: string | undefined,
  opts: { withSec: boolean } = { withSec: false },
): string => {
  if (!iso) {
    return '';
  }
  return DateTime.fromISO(iso).toFormat(`hh:mm${opts.withSec ? ':ss' : ''} a ZZZZ`);
};

/** @return {string} ex: 12:00 PM, 8:01 PM */
export const formatIsoToLocalTime = (iso: string | undefined): string => {
  if (!iso) {
    return '';
  }
  return DateTime.fromISO(iso).toFormat(`h:mm a`);
};

export const formatScheduleAsFullTimeStamp = (
  scheduleDate: string,
  scheduleTime?: string,
  timeZone?: string | null,
): string => {
  if (!scheduleTime) {
    return formatIsoDate(scheduleDate);
  }
  let dateTime = DateTime.fromISO(scheduleTime);
  if (timeZone) {
    dateTime = dateTime.setZone(timeZone);
  }
  return dateTime.toFormat('LL/dd/yyyy hh:mm a ZZZZ');
};

export const formatScheduleTimeAsHHMMSS = (scheduleTime?: string, timeZone?: string): string | undefined => {
  if (!scheduleTime) {
    return;
  }
  let dateTime = DateTime.fromISO(scheduleTime);
  if (timeZone) {
    dateTime = dateTime.setZone(timeZone);
  }
  return dateTime.toFormat('HH:mm:ss');
};

export const formatScheduleDateAtTime = (scheduleDate: string, scheduleTime?: string): string => {
  if (!scheduleTime) {
    return DateTime.fromISO(scheduleDate).toUTC().toFormat('LLL. d, y');
  }
  const dateTime = DateTime.fromISO(scheduleTime);

  return dateTime.toFormat("LLL. d, y 'at' h:mm a");
};

/** Format iso timestamp (ex: '2024-02-23T01:48:23.781Z') to local date (ex: '02/22/2024') */
export const formatIsoTimestampAsLocalDate = (isoTimestamp: string): string => {
  return DateTime.fromISO(isoTimestamp).toLocal().toFormat('LL/dd/yyyy');
};

/** @return ex: Nov 4, 2022 */
export const formatLocalizedScheduleDate = (scheduleDate: string): string => {
  return DateTime.fromISO(scheduleDate).toFormat('DD');
};

/** @return ex: 12-1PM EDT */
export const formatTimeSlot = (scheduleTime: string): string => {
  const start = DateTime.fromISO(scheduleTime);
  const end = DateTime.fromISO(scheduleTime).plus({ hours: 1 });

  // Check if AM/PM periods are different
  const startPeriod = start.toFormat('a');
  const endPeriod = end.toFormat('a');

  // Only show AM/PM for start time if periods differ
  return startPeriod !== endPeriod
    ? `${start.toFormat('h')}${startPeriod}-${end.toFormat('ha ZZZZ')}`
    : `${start.toFormat('h')}-${end.toFormat('ha ZZZZ')}`;
};

/** @return ex: "11-12AM, 1-2PM, 3-4PM CST" */
export const formatTimeSlotList = (scheduleDate: string, timeSlots: string[], timeZone: string): string => {
  const list = timeSlots.map(timeSlot => {
    const start = DateTime.fromISO(`${scheduleDate}T${timeSlot}:00`, { zone: timeZone }).setZone(
      Settings.defaultZoneName,
    );
    const end = start.plus({ hours: 1 });
    return `${start.toFormat('h')}-${end.toFormat('ha')}`;
  });
  const result = list.join(', ') + DateTime.fromISO(scheduleDate).toLocal().toFormat(' ZZZZ');
  return result.includes('Invalid DateTime') ? 'Invalid DateTime' : result;
};

// assumes last name is last in fullName
export const getInitials = (fullName: string): string => {
  const names = fullName.trim().split(' ');
  const firstName = names[0];
  const lastName = names[names.length - 1];
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  return names.length > 1 ? `${firstName[0]}${lastName[0]}` : firstName[0];
};

// truncates a message down to 50 characters
// only what is before a new line character is returned if new line is less
// than 50 characters in
export const truncateMessage = (message: string, length = 50): string => {
  const newLine = '<br/>';
  if (message.includes(newLine)) {
    // ensure note preview is what is before the first new line if existing
    return `${message.split(newLine)[0].slice(0, length)}...`;
  } else {
    const slicedMessage = message.slice(0, length);
    const addEllipsis = slicedMessage.length < message.length;
    return addEllipsis ? `${slicedMessage}...` : message;
  }
};

/**
 * Format a luxon Duration and returns at most `unitsToShow` number of time components.
 * @param duration
 * @param opts - unitsToShow: number of time components to show
 * Ex: when unitsToShow=1, output can be  '5 days' or '2 hours' or 'less than a second'
 * Ex: when unitsToShow=2, output can be  '5 days 3 hours' or '2 hours 3 minutes' or 'less than a second'
 * */
export const formatDuration = (duration: Duration, opts: { unitsToShow: number }): string => {
  const components: DurationUnit[] = ['days', 'hours', 'minutes', 'seconds'];
  const componentTexts: string[] = [];
  const shifted = duration.shiftTo('days', 'hours', 'minutes', 'seconds');
  components.map(component => {
    if (shifted.get(component) >= 1) {
      componentTexts.push(`${Math.floor(shifted.get(component))} ${component}`);
    }
  });

  const text = componentTexts.slice(0, opts.unitsToShow).join(' ');
  return text || 'less than a second';
};

export function formatElapsedTime(timestamp: DateTime, opts: { unitsToShow: number }): string {
  return formatDuration(DateTime.utc().diff(timestamp), opts) + ' ago';
}

export const formatFullName = ({
  firstName,
  lastName,
  middleName,
  suffix,
}: {
  firstName?: string | undefined;
  middleName?: string | null | undefined;
  lastName?: string | undefined;
  suffix?: string | null | undefined;
}): string => {
  return [firstName, middleName, lastName, suffix].filter(Boolean).join(' ');
};
